/**
 * userNotifications.js
 * Manages per-user notification inbox stored at:
 *   users/{uid}/notifications/{notifId}
 *
 * Notification shape:
 *   { type, message, fromName, fromAvatar, postId, createdAt, read }
 *
 * Types:
 *   "reaction"   – someone reacted to your post
 *   "comment"    – someone commented on your post
 *   "thread"     – someone commented on a post you also commented on
 *   "mention"    – you were @mentioned
 */

import { db } from "./firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  onSnapshot,
  writeBatch,
  doc,
  updateDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";

// Dedup guard — prevent double-writes in React StrictMode
const _recentKeys = new Set();

export async function writeUserNotification({
  toUserId,
  type,
  message,
  fromName = "",
  fromAvatar = "",
  postId = "",
  screen = "",   // e.g. "admin_broadcasts", "admin_quests" — for navigation buttons
}) {
  if (!toUserId) return;

  const dedupKey = `${toUserId}|${type}|${postId}|${screen}|${fromName}|${Math.floor(Date.now() / 3000)}`;
  if (_recentKeys.has(dedupKey)) return;
  _recentKeys.add(dedupKey);
  setTimeout(() => _recentKeys.delete(dedupKey), 3000);

  try {
    await addDoc(collection(db, "users", toUserId, "notifications"), {
      type,
      message,
      fromName,
      fromAvatar,
      postId,
      screen,
      read: false,
      createdAt: serverTimestamp(),
    });
  } catch (err) {
    console.warn("[userNotifications] write failed:", err?.code, err?.message);
  }
}

/**
 * Subscribe to a user's notifications (real-time).
 * Returns an unsubscribe function.
 */
export function subscribeToUserNotifications(uid, callback, maxItems = 50) {
  if (!uid) return () => {};
  // No orderBy to avoid requiring a composite index — sort client-side instead
  const q = query(
    collection(db, "users", uid, "notifications"),
    limit(maxItems)
  );
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    // Sort newest first client-side
    items.sort((a, b) => {
      const aMs = a.createdAt?.toMillis?.() ?? a.createdAt?.seconds * 1000 ?? 0;
      const bMs = b.createdAt?.toMillis?.() ?? b.createdAt?.seconds * 1000 ?? 0;
      return bMs - aMs;
    });
    callback(items);
  }, (err) => {
    console.warn("[userNotifications] snapshot error:", err?.code, err?.message);
    callback([]);
  });
}

/**
 * Mark all unread notifications as read for a user.
 */
export async function markAllUserNotificationsRead(uid) {
  if (!uid) return;
  try {
    const q = query(
      collection(db, "users", uid, "notifications"),
      where("read", "==", false)
    );
    const snap = await getDocs(q);
    if (snap.empty) return;
    const batch = writeBatch(db);
    snap.docs.forEach((d) => {
      batch.update(doc(db, "users", uid, "notifications", d.id), { read: true });
    });
    await batch.commit();
  } catch (err) {
    console.warn("markAllUserNotificationsRead failed:", err?.message);
  }
}

/**
 * Mark a single notification as read.
 */
export async function markUserNotificationRead(uid, notifId) {
  if (!uid || !notifId) return;
  try {
    await updateDoc(doc(db, "users", uid, "notifications", notifId), { read: true });
  } catch (err) {
    console.warn("markUserNotificationRead failed:", err?.message);
  }
}

/**
 * Human-readable "time ago" helper (same as adminActivity).
 */
export function notifTimeAgo(timestamp) {
  if (!timestamp) return "";
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Emoji for each notification type.
 */
export function notifEmoji(type) {
  switch (type) {
    case "reaction":        return "❤️";
    case "comment":         return "💬";
    case "thread":          return "🔔";
    case "mention":         return "📣";
    case "call_poll":        return "📊";
    case "call_scheduled":   return "📅";
    case "call_reminder":    return "⏰";
    case "challenge_ended":  return "🏆";
    case "challenge_progress": return "💪";
    default:                 return "🔔";
  }
}
